import axiosInstance from './AxiosInstance';

/**
 * A simple wrapper around the /collections endpoints as defined in API guide.
 * The structure and error handling is modelled after LoansService so that the
 * rest of the application can consume it in the same way.
 */

const CollectionService = {
    /**
     * Fetch a paginated list of collections.
     * @param {Object} params - query parameters: page, limit, search, etc.
     */
    getCollections: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/collections', { params });
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch collections';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },

    /**
     * Fetch collections for a specific loan id.
     * @param {number|string} loanId
     * @param {Object} params
     */
    getCollectionsByLoan: async (loanId, params = {}) => {
        try {
            const response = await axiosInstance.get(
                `/collections/loan/${loanId}`,
                { params },
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch collections for loan';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },

    /**
     * Create a new collection record.
     * @param {Object} payload
     */
    createCollection: async (payload) => {
        try {
            const response = await axiosInstance.post('/collections', payload);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to create collection';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },

    /**
     * Update only the card status of a collection.
     * @param {number|string} id
     * @param {string} cardStatus
     */
    updateCardStatus: async (id, cardStatus, cardReturnedDate = null, useQueryParam = false) => {
        try {
            const payload = { cardStatus };
            // Only include cardReturnedDate if provided (nullable allowed)
            if (cardReturnedDate !== undefined) payload.cardReturnedDate = cardReturnedDate;
            // Optionally append query parameter updateCardStatus=true when caller requests it
            const url = useQueryParam
                ? `/collections/${id}/card-status?updateCardStatus=true`
                : `/collections/${id}/card-status`;
            const response = await axiosInstance.patch(url, payload);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to update card status';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },

    /**
     * Update the various charges fields of a collection.
     * @param {number|string} id
     * @param {Object} chargesPayload
     */
    updateCharges: async (id, chargesPayload) => {
        try {
            const response = await axiosInstance.patch(
                `/collections/${id}/charges`,
                chargesPayload,
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to update charges';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },

    /**
     * Record seizer payment details (charges and chargePaidDate)
     * PATCH /collections/:id/seizer-payment
     * @param {number|string} id
     * @param {number} charges
     * @param {string|null} chargePaidDate - YYYY-MM-DD or null
     */
    updateSeizerPayment: async (id, chargePaidDate, charges) => {
        try {
            const payload = { charges };
            // include chargePaidDate if explicitly provided (nullable allowed)
            if (chargePaidDate !== undefined) payload.chargePaidDate = chargePaidDate;
            const response = await axiosInstance.patch(
                `/collections/${id}/seizer-payment`,
                payload,
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to update seizer payment';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },

    /**
     * Get collection by its ID (not in original API guide but convenient)
     */
    getCollectionById: async (id) => {
        try {
            const response = await axiosInstance.get(`/collections/${id}`);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch collection';
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },
    /**
     * Check whether the card for a loan is returned.
     * GET /collections/loan/:loanId/card-returned
     * @param {number|string} loanId
     */
    isCardReturnedByLoan: async (loanId, useQueryParam = false) => {
        try {
            const url = useQueryParam
                ? `/collections/loan/${loanId}/card-returned?updateCardStatus=true`
                : `/collections/loan/${loanId}/card-returned`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to check card returned status';
            throw {
                message: errorMessage,
                validationErrors: error.response?.data?.errors || [],
                status: error.response?.status,
            };
        }
    },
};

export default CollectionService;
